import { Braces, Check, Info } from 'lucide-react';
import type { TemplateVariable } from '../../hooks/useVariableInsertion';

interface VariableInsertPanelProps {
  variables: TemplateVariable[];
  showVariables: boolean;
  onToggle: () => void;
  onInsert: (varName: string) => void;
  insertedVar: string | null;
}

export function VariableInsertPanel({
  variables,
  showVariables,
  onToggle,
  onInsert,
  insertedVar,
}: VariableInsertPanelProps) {
  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onToggle}
          className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-all duration-200 ${
            showVariables
              ? 'bg-[#05E0D1]/10 text-[#0bb5a5] border border-[#05E0D1]/30'
              : 'text-gray-600 hover:text-[#0bb5a5] hover:bg-[#05E0D1]/5 border border-transparent'
          }`}
        >
          <Braces className="w-4 h-4" />
          Add Variables
        </button>
        <div className="group relative">
          <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 z-10">
            Personalize your message by adding variables
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
          </div>
        </div>
      </div>

      {showVariables && (
        <div className="p-3 bg-gradient-to-br from-gray-50/80 to-[#05E0D1]/[0.03] border border-gray-200 rounded-xl">
          <div className="flex flex-wrap gap-1.5">
            {variables.map((v) => (
              <button
                key={v.name}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onInsert(v.name)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 cursor-pointer ${
                  insertedVar === v.name
                    ? 'bg-[#05E0D1]/15 text-[#0bb5a5] border border-[#05E0D1]/40 scale-[1.03]'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-[#05E0D1]/40 hover:bg-[#05E0D1]/[0.04] hover:text-[#0bb5a5] hover:shadow-sm'
                }`}
              >
                <code className="font-mono text-[11px] tracking-tight">{v.name}</code>
                {insertedVar === v.name && (
                  <Check className="w-3 h-3 text-[#05E0D1]" />
                )}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-2">Click a variable to insert it at the cursor position</p>
        </div>
      )}
    </>
  );
}
